import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  ClipboardList,
  Briefcase,
  Users,
  UserCog,
  LogOut,
  Menu,
} from "lucide-react";
import { useAuth } from "../auth/AuthContext";

const MOBILE_BREAKPOINT_PX = 900;

const navItems = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/bookings", label: "Bookings", icon: ClipboardList },
  { to: "/jobs", label: "Jobs", icon: Briefcase },
  { to: "/employees", label: "Employees", icon: Users },
  { to: "/users", label: "Users", icon: UserCog },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const getIsMobile = () =>
    typeof window !== "undefined" && typeof window.matchMedia === "function"
      ? window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`).matches
      : false;

  const [isMobile, setIsMobile] = useState(() => getIsMobile());
  const [sidebarOpen, setSidebarOpen] = useState(() => (getIsMobile() ? false : true));

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mediaQuery = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX}px)`);
    const onChange = (event) => {
      setIsMobile(event.matches);
      if (event.matches) setSidebarOpen(false);
    };

    setIsMobile(mediaQuery.matches);
    if (mediaQuery.matches) setSidebarOpen(false);

    if (mediaQuery.addEventListener) mediaQuery.addEventListener("change", onChange);
    else mediaQuery.addListener(onChange);

    return () => {
      if (mediaQuery.removeEventListener) mediaQuery.removeEventListener("change", onChange);
      else mediaQuery.removeListener(onChange);
    };
  }, []);

  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
  }, [isMobile, location.pathname]);

  const pageTitle = useMemo(() => {
    const match = navItems.find((item) => location.pathname.startsWith(item.to));
    return match?.label || "Royal Shine Admin";
  }, [location.pathname]);

  return (
    <div className="rs-shell">
      {isMobile && sidebarOpen ? (
        <button
          className="rs-backdrop"
          aria-label="Close navigation"
          type="button"
          onClick={() => setSidebarOpen(false)}
        />
      ) : null}

      <aside className={`rs-sidebar ${sidebarOpen ? "is-open" : "is-collapsed"}`}>
        <div className="rs-sidebar__brand">
          <div className="rs-logo">
            Royal <span>Shine</span>
          </div>
          <div className="rs-subtitle">Admin Panel</div>
        </div>

        <nav className="rs-nav">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `rs-nav__item ${isActive ? "is-active" : ""}`
                }
              >
                <Icon size={18} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
        </nav>

        <div className="rs-sidebar__footer">
          <div className="rs-user">
            <div className="rs-user__label">Signed in</div>
            <div className="rs-user__value">{user?.username || "Admin"}</div>
          </div>
          <button className="rs-btn rs-btn--ghost rs-btn--full" onClick={logout}>
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </div>
      </aside>

      <div className="rs-main">
        <header className="rs-topbar">
          <button
            className="rs-icon-btn"
            onClick={() => setSidebarOpen((v) => !v)}
            aria-label="Toggle navigation"
            aria-expanded={sidebarOpen}
            title="Toggle navigation"
          >
            <Menu size={18} />
          </button>
          <div className="rs-topbar__title">{pageTitle}</div>
        </header>

        <main className="rs-content">
          <Outlet />
        </main>

        <nav className="rs-bottom-nav" aria-label="Primary">
          {navItems.map((item) => {
            const Icon = item.icon;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                  `rs-bottom-nav__item ${isActive ? "is-active" : ""}`
                }
              >
                <Icon size={18} />
                <span>{item.label}</span>
              </NavLink>
            );
          })}
          <button className="rs-bottom-nav__item" type="button" onClick={logout}>
            <LogOut size={18} />
            <span>Logout</span>
          </button>
        </nav>
      </div>
    </div>
  );
}
